/**
 * Promise utility functions for handling common async operations
 */

/**
 * Executes a promise operation with automatic cancellation error handling
 *
 * Ignores both AbortError (modern standard) and CanceledError (Axios CancelToken) silently,
 * allowing other errors to propagate normally.
 *
 * @param operation - The async operation to execute
 * @returns The result of the operation or null if cancelled
 *
 * @example
 * ```typescript
 * const result = await executeWithCancellation(() =>
 *   fetch('/api/data').then(r => r.json())
 * );
 *
 * if (result) {
 *   // Handle successful result
 *   console.log(result);
 * }
 * // Cancelled requests are handled automatically
 * ```
 */
const executeWithCancellation = async <T>(
  operation: () => Promise<T>
): Promise<T | null> => {
  try {
    return await operation();
  } catch (error) {
    if (
      error instanceof Error &&
      (error.name === 'AbortError' || error.name === 'CanceledError')
    ) {
      return null;
    }

    throw error;
  }
};

export default executeWithCancellation;
